import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Lock, LinkIcon, Clock, ShieldCheck } from 'lucide-react';
import MyCleanStudioHeader from './MyCleanStudioHeader';
import MyCleanStudioFooter from './MyCleanStudioFooter';
import ScanReportBody from './report/ScanReportBody';
import shareService from '../features/sharing/shareService';
import { track } from '../analytics/analytics';
import youtubeIcon from '../assets/youtube_icon.png';
import tiktokIcon from '../assets/tiktok_icon.png';
import instagramIcon from '../assets/insta_icon.png';
import './SharedReportPage.css';

const PLATFORM_ICONS = { youtube: youtubeIcon, tiktok: tiktokIcon, instagram: instagramIcon };

const formatDay = (iso) => {
  if (!iso) return '';
  // Backend timestamps without a zone are UTC.
  const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(normalized);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Public, read-only view of a shared scan report at /r/:shareId.
 * No login. Locked shares ask for the PIN first.
 */
function SharedReportPage() {
  const { shareId } = useParams();
  const [state, setState] = useState({ status: 'loading' });
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    shareService.viewSharedReport(shareId).then((result) => {
      if (cancelled) return;
      setState(result);
      // Viewer side of the loop: did the link open, or was it locked/dead?
      track('shared_report_opened', { outcome: result.status });
    });
    return () => { cancelled = true; };
  }, [shareId]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (pin.length !== 6 || submitting) return;
    setSubmitting(true);
    const result = await shareService.viewSharedReport(shareId, pin);
    setSubmitting(false);
    setState(result);
    track('shared_report_pin_entered', { outcome: result.status });
    if (result.status !== 'ok') setPin('');
  };

  const report = state.status === 'ok' ? state.report : null;
  const platform = (report?.platform || 'youtube').toLowerCase();
  const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);

  return (
    <div className="shared-page">
      {/* Keep shared reports out of search results, and keep the link code
          out of the Referer header when viewers click through to guidelines. */}
      <title>{report ? `${report.title} — Content check report` : 'Shared report'} | MyCleanStudio</title>
      <meta name="robots" content="noindex, nofollow" />
      <meta name="referrer" content="no-referrer" />

      <MyCleanStudioHeader />

      <main className="shared-content">
        {state.status === 'loading' && (
          <div className="shared-center">
            <div className="shared-spinner" />
            <p>Loading report…</p>
          </div>
        )}

        {(state.status === 'pin_required' || state.status === 'invalid_pin') && (
          <div className="shared-card">
            <div className="shared-card-icon"><Lock size={26} /></div>
            <h1 className="shared-card-title">This report is locked</h1>
            <p className="shared-card-text">Enter the 6-digit PIN you were sent with the link.</p>
            <form onSubmit={handleUnlock} className="shared-pin-form">
              <input
                className="shared-pin-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                aria-label="PIN"
                placeholder="••••••"
                autoFocus
              />
              <button className="shared-btn" type="submit" disabled={pin.length !== 6 || submitting}>
                {submitting ? 'Checking…' : 'View report'}
              </button>
            </form>
            {state.status === 'invalid_pin' && (
              <p className="shared-error">
                Incorrect PIN.
                {state.attemptsLeft != null && ` ${state.attemptsLeft} ${state.attemptsLeft === 1 ? 'try' : 'tries'} left.`}
              </p>
            )}
          </div>
        )}

        {state.status === 'locked_out' && (
          <div className="shared-card">
            <div className="shared-card-icon"><Clock size={26} /></div>
            <h1 className="shared-card-title">Too many wrong PINs</h1>
            <p className="shared-card-text">
              For security this report is paused for
              {' '}{Math.max(1, Math.ceil((state.retryAfterSeconds || 900) / 60))} minutes.
              Check the PIN with the person who shared it, then try again.
            </p>
          </div>
        )}

        {(state.status === 'not_found' || state.status === 'error') && (
          <div className="shared-card">
            <div className="shared-card-icon"><LinkIcon size={26} /></div>
            <h1 className="shared-card-title">
              {state.status === 'not_found' ? 'This link is no longer available' : 'Something went wrong'}
            </h1>
            <p className="shared-card-text">
              {state.status === 'not_found'
                ? 'It may have expired, or the owner stopped sharing it. Ask them for a new link.'
                : 'We couldn’t load this report. Please try again in a moment.'}
            </p>
          </div>
        )}

        {report && (
          <div className="history-detail-report shared-report">
            <div className="shared-report-banner">
              <ShieldCheck size={16} />
              <span>Shared read-only content check report</span>
            </div>

            <div className="history-report-header">
              <div className="history-report-header-left">
                <h1 className="history-report-title">{report.title}</h1>
                <div className="history-report-platform">
                  <img
                    src={PLATFORM_ICONS[platform] || youtubeIcon}
                    alt={platformName}
                    className="history-report-platform-icon"
                  />
                  <span>{platformName}</span>
                  {report.scanned_on && (
                    <span className="shared-report-date">· Scanned {formatDay(report.scanned_on)}</span>
                  )}
                </div>
              </div>
            </div>

            <ScanReportBody data={report.data} platform={platform} />

            <div className="shared-cta">
              <h2>Check your own videos before you publish</h2>
              <p>
                MyCleanStudio scans videos against YouTube and TikTok guidelines — policy
                issues, copyrighted music and earnings risks — in minutes.
              </p>
              <Link
                to="/"
                className="shared-btn"
                onClick={() => track('shared_report_cta_clicked')}
              >
                Try it free
              </Link>
            </div>
          </div>
        )}
      </main>

      <MyCleanStudioFooter />
    </div>
  );
}

export default SharedReportPage;
